/** `[timestampMs, value]` chart point. */
export type ChartPoint = [number, number]

export interface NavValue {
  time: string | number | Date
  price?: number
  scudo?: number
  /** The month's return in gold, %, as the fund publishes it (Goldstream). */
  alpha?: number
}

/**
 * One day of `/prices/bmnMined` (BMN1, BMN2), per note. Amounts in BTC come as strings or
 * numbers. BMN1 has no `remaining_hashprice_in_btc`: it matured.
 */
export interface BmnMinedRow {
  time: string
  amount?: string | number
  cumulativeRewards?: string | number
  cumulative_in_usd?: number
  cumulative_in_eur?: number
  btc_in_usd?: number
  btc_in_eur?: number
  remaining_hashprice_in_btc?: number
  days_remaining?: number
}

/** A project's tokens in circulation on a day (`/public/projects/tokens/supply`); `date` is `YYYY-MM-DD`. */
export interface TokenSupplyRow {
  date: string
  tokens: number
}

/** `YYYY-MM-DD` (UTC) of a time. */
const utcDay = (time: string | number | Date): string => new Date(time).toISOString().slice(0, 10)

/**
 * Where a fund's units are asked from: before STOKR's first fund. Fixed, not the fund's first NAV, so
 * the request runs alongside the NAV instead of after it (the days before a fund's first units are
 * left out anyway); its own dates (opening time, sales) are not reliable for this.
 */
export const FUND_UNITS_FROM = '2021-01-01'

/**
 * A fund's units outstanding (its token supply) over time, kept only where the count changes —
 * units move on dealing days, so a few dozen rows stand for years of daily ones (small enough
 * to server-render). `through`: the last day asked (today when fetched).
 */
export interface UnitsOutstanding {
  changes: { date: string; units: number }[]
  through: string
}

export const toUnitsOutstanding = (rows: TokenSupplyRow[]): UnitsOutstanding | undefined => {
  const daily = rows
    .filter((row) => typeof row.date === 'string' && Number.isFinite(Number(row.tokens)))
    .sort((a, b) => a.date.localeCompare(b.date))
  if (!daily.length) return undefined
  const changes: UnitsOutstanding['changes'] = []
  for (const row of daily) {
    const units = Number(row.tokens)
    if (changes[changes.length - 1]?.units !== units) changes.push({ date: row.date, units })
  }
  return { changes, through: daily[daily.length - 1].date }
}

/** Units outstanding on a day (`YYYY-MM-DD`): the last change on or before it. */
const unitsOn = (units: UnitsOutstanding, day: string): number | undefined => {
  let found: number | undefined
  for (const change of units.changes) {
    if (change.date > day) break
    found = change.units
  }
  return found
}

/**
 * The fund's total NAV: each unit NAV × the units outstanding on its date (the same day matched
 * the funds' own reports best). Before there are units there is no total, so those dates are left
 * out. When the units have changed since the latest NAV, the total at that NAV with today's units
 * closes the line, dated `through`: the current figure.
 */
export const toTotalNavPoints = (unitNav: ChartPoint[], units: UnitsOutstanding): ChartPoint[] => {
  const points = unitNav.flatMap(([time, value]): ChartPoint[] => {
    const count = unitsOn(units, utcDay(time))
    return count ? [[time, value * count]] : []
  })
  const latest = unitNav[unitNav.length - 1]
  const today = unitsOn(units, units.through)
  if (latest && today && units.through > utcDay(latest[0]) && today !== unitsOn(units, utcDay(latest[0]))) {
    points.push([new Date(`${units.through}T00:00:00Z`).getTime(), latest[1] * today])
  }
  return points
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The units outstanding on every day from the first with any to `through`: a note's supply as a daily
 * series, so its chart has a point a day (as rwa.xyz) and every range has data.
 */
export const dailyUnits = (units: UnitsOutstanding): ChartPoint[] => {
  const first = units.changes.find((change) => change.units > 0)
  if (!first) return []
  const points: ChartPoint[] = []
  const end = Date.parse(`${units.through}T00:00:00Z`)
  for (let time = Date.parse(`${first.date}T00:00:00Z`); time <= end; time += DAY_MS) {
    const count = unitsOn(units, utcDay(time))
    if (count !== undefined) points.push([time, count])
  }
  return points
}
